@file:OptIn(ExperimentalResourceApi::class)

package ui.user.detail

import androidx.compose.runtime.*
import common.frontend.api.*
import compose.assets.Svg
import compose.assets.svg.Information_slab_circle_outline
import compose.assets.svg.Key
import io.Repository
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.ExperimentalResourceApi
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.navTo_LoginUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.utils.DataState
import tk.mallumo.utils.mutableDataState
import tools.TabEntry
import tools.await
import tools.findRoot
import tools.printStackTraceInDebug
import ui.snackBarStateGlobal

@VM
class UserVM : NavigationViewModel() {

    private lateinit var scopeUi: UserScope

    enum class TabType {
        NONE,
        INFO,
        PERMISSION_OWN,
        PERMISSION_OTHER
    }

    var state by mutableDataState<UserScope.Wrapper>()
        private set

    var tabsScreenSmall by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    var tabsScreenLarge by mutableStateOf(listOf<TabEntry<TabType>>())
        private set

    private val _selectedTabSmall = MutableStateFlow(TabType.NONE)
    val selectedTabSmall: StateFlow<TabType> get() = _selectedTabSmall


    private val _selectedTabLarge = MutableStateFlow(TabType.NONE)
    val selectedTabLarge: StateFlow<TabType> get() = _selectedTabLarge

    sealed interface Action {
        data object Delete : Action
        data object Save : Action
        data object ResetPassword : Action
        companion object {


            @Composable
            fun rememberAction(vm: UserVM): (Action) -> Unit {
                return remember {
                    { act ->
                        when (act) {
                            Save -> vm.save()
                            ResetPassword -> vm.resetPassword()
                            Delete -> vm.delete()
                        }
                    }
                }
            }
        }
    }

    private fun delete() {
        scope.launch {
            runCatching {
                Repository.api.user.delete(scopeUi.args.userId)
            }.onSuccess {
                scopeUi.nav.up()
            }.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    it.message!!,
                    withDismissAction = true
                )
            }
        }
    }

    private fun resetPassword() {
        scope.launch {
            runCatching {
                Repository.api.user.passwordReset(scopeUi.args.userId)
            }.onFailure {
                it.printStackTraceInDebug()
                snackBarStateGlobal.showSnackbar(
                    it.message!!,
                    withDismissAction = true
                )
            }.onSuccess {
                if (scopeUi.args.userId == scopeUi.login.id) {
                    scopeUi.account.logout()
                    scopeUi.nav.findRoot().navTo_LoginUI(clearTop = true)
                }
            }
        }
    }

    private fun save() {
        scope.launch {
            state.entry
                ?.runCatching {
                    val permissionChanged = permissionOwn.changed || permissionOther.changed
                    if (permissionOwn.changed) permissionOwn.applyChanges()
                    if (permissionOther.changed) permissionOther.applyChanges()

                    if (permissionChanged) {
                        Repository.api.user.editPermission(data.id, data.permission)
                        permissionOwn.updateOrigin()
                        permissionOther.updateOrigin()
                    }

                    if (change) Repository.api.user.edit(data)
                    this
                }
                ?.onFailure {
                    it.printStackTraceInDebug()
                    snackBarStateGlobal.showSnackbar(
                        it.message!!,
                        withDismissAction = true
                    )
                }
                ?.onSuccess { it.updateOrigin() }
        }
    }


    fun init(scopeUi: UserScope) {
        this.scopeUi = scopeUi
        if (state !is DataState.Result) {
            state = DataState.Loading()
        }
        scope.launch {
            runCatching {
                val user = Repository.api.user.single(scopeUi.args.userId)
                val permissionNotMy = scopeUi.login.id != user.id

                val canPermissionEditOwn = scopeUi.login.canUserPermissionEditOwnCompany(user.company.id)
                val canPermissionEditOther: Boolean = scopeUi.login.canUserPermissionEditOthersCompany(user.company.id)
                val isTargetUserSuperAdmin = user.id in arrayOf(1006L, 11006L)
                configureTabs(scopeUi)

                val title = Res.string.user_detail__title.await().let {
                    if (scopeUi.login.company.id != user.company.id) "$it (${user.id}) " to user.company.name
                    else it to "(${user.id})"
                }

                state = DataState.Result(
                    UserScope.Wrapper(
                        login = scopeUi.login,
                        canEdit = scopeUi.login.canUserEdit(user.company.id),
                        canDelete = scopeUi.login.canUserDelete(user.company.id) && user.id != scopeUi.login.id,
                        title = title,
                        canPermissionEditOwn = (permissionNotMy && canPermissionEditOwn && !isTargetUserSuperAdmin) || scopeUi.login.id == 1006L,
                        canPermissionEditOther = (permissionNotMy && canPermissionEditOther && !isTargetUserSuperAdmin) || scopeUi.login.id == 1006L,
                        data = user
                    )
                )
            }.onFailure {
                it.printStackTrace()
                state = DataState.Error(Res.string.err_connection_interrupted.await())
            }
        }
    }

    private fun configureTabs(scopeUi: UserScope) {
        tabsScreenSmall = buildTabs(true, scopeUi) {
            _selectedTabSmall.value = it.tag
        }
        tabsScreenLarge = buildTabs(false, scopeUi) {
            _selectedTabLarge.value = it.tag
        }
        _selectedTabSmall.value = tabsScreenSmall.first().tag
        _selectedTabLarge.value = tabsScreenLarge.firstOrNull()?.tag ?: TabType.NONE
    }

    private fun buildTabs(
        withMainScreen: Boolean,
        scopeUi: UserScope,
        onClick: (TabEntry<TabType>) -> Unit
    ): List<TabEntry<TabType>> {
        return buildList {
            if (withMainScreen) {
                add(
                    TabEntry(
                        icon = Svg.Information_slab_circle_outline,
                        label = Res.string.user_detail__info__title,
                        onClick = onClick,
                        tag = TabType.INFO
                    )
                )
            }
            if (scopeUi.login.permission.canUserPermission(Io.Permission.OWN_VIEW)) {
                add(
                    TabEntry(
                        icon = Svg.Key,
                        label = Res.string.user_detail__permission_inner__title,
                        onClick = onClick,
                        tag = TabType.PERMISSION_OWN
                    )
                )
            }
            if (scopeUi.login.permission.canUserPermission(Io.Permission.OTHERS_VIEW)) {
                add(
                    TabEntry(
                        icon = Svg.Key,
                        label = Res.string.user_detail__permission_outer__title,
                        onClick = onClick,
                        tag = TabType.PERMISSION_OTHER
                    )
                )
            }
        }
    }

    override fun onRelease() {
        state = DataState.Idle()
    }

}
